/**
 * 用户提示词管理页面
 * 支持系统默认/自定义两种模式
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { userPromptsApi } from '../services/userPromptsApi';
import { toast, confirm } from '../store/toastStore';
import PageHeader from '../components/layout/PageHeader';

interface PromptConfig {
  key: string;
  name: string;
  category: string;
  has_user: boolean;
  mode: 'system' | 'custom';
  system_version: number;
  version: number;
  content_system: string;
  content_user: string | null;
  variables_user: string[];
}

interface VersionItem {
  id: number;
  version: number;
  content_system: string;
  content_user: string | null;
  is_active: boolean;
  created_at: string | null;
}

type DocType = 'bid' | 'proposal';

const CATEGORY_LABELS: Record<string, string> = {
  document_analysis: '文档分析',
  outline: '大纲生成',
  content: '内容生成',
};

const UserPrompts: React.FC = () => {
  const [docType, setDocType] = useState<DocType>('bid');
  const [prompts, setPrompts] = useState<PromptConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [switchModal, setSwitchModal] = useState<{ key: string; name: string } | null>(null);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [unsaved, setUnsaved] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState<string>('');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  // 系统原始内容快照：取消编辑时恢复用
  const systemSnapshot = useRef<Record<string, PromptConfig>>({});

  const loadPrompts = useCallback(async (dt: DocType) => {
    setLoading(true);
    try {
      const res = await userPromptsApi.getUserPrompts(dt);
      const data = res.data as PromptConfig[];
      // 强制取消所有编辑状态：刷新后全部回到系统默认
      const resetData = data.map(p => ({ ...p, mode: 'system' as const }));
      setPrompts(resetData);
      setUnsaved(new Set());
      // 保存系统原始内容快照（用于取消编辑时恢复）
      const snap: Record<string, PromptConfig> = {};
      data.forEach(p => { snap[p.key] = { ...p }; });
      systemSnapshot.current = snap;
    } catch (e) {
      console.error('加载提示词失败', e);
      toast.error('加载提示词失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts(docType);
  }, [docType, loadPrompts]);

  const updateContent = (key: string, field: 'content_system' | 'content_user', value: string) => {
    setPrompts(prev => prev.map(p => p.key === key ? { ...p, [field]: value } : p));
    setUnsaved(prev => new Set(prev).add(key));
  };

  const handleSave = async (p: PromptConfig) => {
    if (!unsaved.has(p.key)) {
      toast.info('内容未发生变化，已取消编辑');
      // 自动结束编辑状态
      await handleSwitchToSystem(p.key);
      return;
    }
    setSaving(p.key);
    try {
      const res = await userPromptsApi.saveVersion(p.key, {
        content_system: p.content_system,
        content_user: p.content_user,
      });
      setUnsaved(prev => { const n = new Set(prev); n.delete(p.key); return n; });
      // 只更新该卡片的版本号，不触发全量重渲染导致滚动跳顶
      setPrompts(prev => prev.map(item =>
        item.key === p.key
          ? { ...item, version: (res.data as any).version }
          : item
      ));
      toast.success('已保存');
    } catch (e) {
      console.error('保存失败', e);
      toast.error('保存失败');
    } finally {
      setSaving(null);
    }
  };

  const reloadPreserveScroll = async (dt: DocType) => {
    // 仅保留给 handleApplyVersion 使用
    // 记录当前滚动位置
    const scrollEl = document.querySelector('.flex-1.overflow-y-auto') || document.querySelector('main') || window;
    const y = scrollEl instanceof Window ? scrollEl.scrollY : scrollEl.scrollTop;
    await loadPrompts(dt);
    // 等 React 渲染完成后恢复
    requestAnimationFrame(() => {
      if (scrollEl instanceof Window) scrollEl.scrollTo(0, y);
      else scrollEl.scrollTop = y;
    });
  };

  const handleSwitchToSystem = async (key: string) => {
    if (unsaved.has(key)) {
      if (!await confirm({ title: '未保存内容', message: '有未保存的内容，确定切回系统默认吗？', confirmLabel: '切回默认' })) return;
    }
    try {
      await userPromptsApi.switchMode(key, 'system');
      // 从快照恢复系统默认内容，不触发全量重渲染
      const snap = systemSnapshot.current[key];
      if (snap) {
        setPrompts(prev => prev.map(item =>
          item.key === key
            ? { ...snap, mode: 'system' }
            : item
        ));
        setUnsaved(prev => { const n = new Set(prev); n.delete(key); return n; });
      }
    } catch (e) {
      toast.error('切换失败');
    }
  };

  const handleEdit = async (p: PromptConfig) => {
    // 复制系统默认内容创建自定义版本
    try {
      const res = await userPromptsApi.saveVersion(p.key, {
        content_system: p.content_system,
        content_user: p.content_user,
      });
      // 只更新该卡片为 custom 模式，不触发全量重渲染
      setPrompts(prev => prev.map(item =>
        item.key === p.key
          ? { ...item, mode: 'custom', version: (res.data as any).version }
          : item
      ));
    } catch (e) {
      toast.error('创建自定义版本失败');
    }
  };

  const openSwitchModal = async (key: string, name: string) => {
    setSwitchModal({ key, name });
    try {
      const res = await userPromptsApi.getVersions(key);
      setVersions(res.data as VersionItem[]);
    } catch (e) {
      toast.error('加载版本列表失败');
      setVersions([]);
    }
  };

  const handleApplyVersion = async (key: string, versionId: number) => {
    try {
      await userPromptsApi.switchMode(key, 'custom', versionId);
      setSwitchModal(null);
      await reloadPreserveScroll(docType);
    } catch (e) {
      toast.error('应用版本失败');
    }
  };

  const handleDeleteVersion = async (key: string, versionId: number) => {
    try {
      await userPromptsApi.deleteVersion(key, versionId);
      const res = await userPromptsApi.getVersions(key);
      setVersions(res.data as VersionItem[]);
    } catch (e: any) {
      const msg = e.response?.data?.detail || '删除失败';
      toast.error(msg);
    }
  };

  const grouped = prompts.reduce<Record<string, PromptConfig[]>>((acc, p) => {
    const cat = p.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  const CATEGORY_ORDER = ['document_analysis', 'outline', 'content'];
  const categories = CATEGORY_ORDER.filter(k => grouped[k]);

  const shortName = (fullName: string) => {
    const prefix = docType === 'bid' ? '标书·' : '方案·';
    return fullName.startsWith(prefix) ? fullName.slice(prefix.length) : fullName;
  };

  // Scrollspy
  useEffect(() => {
    if (loading || prompts.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) setActiveKey(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    prompts.forEach(p => {
      const el = document.getElementById(p.key);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, prompts]);

  return (
    <div className="flex gap-6">
      {/* 左侧导航侧边栏 */}
      {!loading && prompts.length > 0 && (
        <div className="w-[220px] shrink-0 hidden lg:block">
          <div className="sticky top-4 space-y-5 max-h-[calc(100vh-6rem)] overflow-y-auto">
            {categories.map(cat => (
              <div key={cat}>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  {CATEGORY_LABELS[cat] || cat}
                  <span className="ml-1 text-muted-foreground font-normal">({grouped[cat].length})</span>
                </div>
                <div className="space-y-0.5">
                  {grouped[cat].map(p => (
                    <button
                      key={p.key}
                      onClick={() => document.getElementById(p.key)?.scrollIntoView({ behavior: 'smooth' })}
                      className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
                        activeKey === p.key
                          ? 'bg-primary/10 text-primary font-medium border-l-[3px] border-blue-500 -ml-[3px]'
                          : 'text-foreground hover:text-foreground hover:bg-background border-l-[3px] border-transparent -ml-[3px]'
                      }`}
                    >
                      <span className="truncate block">{shortName(p.name)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 右侧内容区 */}
      <div ref={scrollContainerRef} className="flex-1 min-w-0 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <PageHeader title="提示词管理 · 我的配置" />
          <div className="flex space-x-2 bg-accent rounded-lg p-1">
            <button onClick={() => setDocType('bid')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${docType === 'bid' ? 'bg-white text-primary shadow-sm' : 'text-foreground'}`}>标书提示词</button>
            <button onClick={() => setDocType('proposal')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${docType === 'proposal' ? 'bg-white text-primary shadow-sm' : 'text-foreground'}`}>方案提示词</button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">加载中...</div>
        ) : (
          categories.map(cat => (
            <div key={cat} className="mb-8">
              <h2 className="text-lg font-semibold text-foreground mb-3 px-1">{CATEGORY_LABELS[cat] || cat}</h2>
              <div className="space-y-4">
                {grouped[cat].map(p => (
                  <div key={p.key} id={p.key} className="bg-white border border-border rounded-xl overflow-hidden">
                    {/* 标题栏 */}
                    <div className="flex items-center justify-between px-5 py-3 bg-background border-b border-border">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-medium text-foreground truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground font-mono truncate hidden sm:inline">{p.key}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          p.mode === 'system' ? 'bg-primary/10 text-primary' : 'bg-green-50 text-green-700'
                        }`}>
                          {p.mode === 'system' ? `系统默认 v${p.system_version}` : `自定义 v${p.version}`}
                        </span>
                      </div>
                    </div>

                    {/* system textarea */}
                    <div className="px-5 pt-3">
                      <div className="text-xs text-muted-foreground mb-1">system</div>
                      <textarea
                        value={p.content_system}
                        onChange={e => p.mode === 'custom' && updateContent(p.key, 'content_system', e.target.value)}
                        readOnly={p.mode === 'system'}
                        className={`w-full border rounded-lg p-3 text-sm font-mono leading-relaxed resize-y ${
                          p.mode === 'system' ? 'bg-background border-border text-muted-foreground' : 'bg-white border-border'
                        }`}
                        rows={20}
                        spellCheck={false}
                      />
                    </div>

                    {/* user textarea */}
                    <div className="px-5 pb-3">
                      <div className="text-xs text-muted-foreground mb-1">user</div>
                      <textarea
                        value={p.content_user || ''}
                        onChange={e => p.mode === 'custom' && updateContent(p.key, 'content_user', e.target.value)}
                        readOnly={p.mode === 'system'}
                        className={`w-full border rounded-lg p-3 text-sm font-mono leading-relaxed resize-y ${
                          p.mode === 'system' ? 'bg-background border-border text-muted-foreground' : 'bg-white border-border'
                        }`}
                        rows={20}
                        spellCheck={false}
                      />
                    </div>

                    {/* 变量区：系统模式和自定义模式都显示 */}
                    {p.has_user && Array.isArray(p.variables_user) && p.variables_user.length > 0 && (
                      <div className="px-5 pb-2">
                        <span className="text-xs text-muted-foreground mr-2">可用变量</span>
                        {p.variables_user.map(v => (
                          p.mode === 'custom' ? (
                            <VariableCopyBtn key={v} varName={v} />
                          ) : (
                            <span key={v} className="inline-block text-xs px-2 py-0.5 rounded mr-1 mb-0.5 bg-accent text-muted-foreground" title={varAnnotations[v] || v}>
                              {`{${v}}`}
                            </span>
                          )
                        ))}
                      </div>
                    )}

                    {/* 操作栏 */}
                    <div className="flex items-center justify-end gap-2 px-5 py-3 bg-background border-t border-border">
                      {p.mode === 'system' ? (
                        <>
                          <button onClick={() => openSwitchModal(p.key, p.name)}
                            className="px-3 py-1.5 text-sm text-foreground hover:text-foreground hover:bg-accent rounded-lg">切为自定义</button>
                          <button onClick={() => handleEdit(p)}
                            className="px-3 py-1.5 text-sm font-medium bg-primary text-white hover:bg-primary/90 rounded-lg">编辑</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleSwitchToSystem(p.key)}
                            className="px-3 py-1.5 text-sm text-foreground hover:text-foreground hover:bg-accent rounded-lg">取消</button>
                          <button onClick={() => openSwitchModal(p.key, p.name)}
                            className="px-3 py-1.5 text-sm text-foreground hover:text-foreground hover:bg-accent rounded-lg">历史</button>
                          <button onClick={() => handleSave(p)}
                            disabled={saving === p.key}
                            className={`px-3 py-1.5 text-sm font-medium rounded-lg ${saving === p.key ? 'bg-primary/50 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}>
                            {saving === p.key ? '保存中...' : '保存'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* 版本切换弹窗 */}
        {switchModal && (
          <div className="fixed inset-0 bg-black/40 flex items-start justify-center pt-20 z-50" onClick={() => setSwitchModal(null)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">我的版本 · {switchModal.name}</h3>
                <button onClick={() => setSwitchModal(null)} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
              </div>
              <div className="p-6">
                {versions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">暂无自定义版本</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-muted-foreground border-b"><th className="pb-2">版本</th><th className="pb-2">更新</th><th className="pb-2 text-right">操作</th></tr></thead>
                    <tbody>
                      {versions.map(v => (
                        <tr key={v.id} className="border-b border-border">
                          <td className="py-3"><span className="font-mono">v{v.version}</span>{v.is_active && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">当前</span>}</td>
                          <td className="py-3 text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleDateString() : '-'}</td>
                          <td className="py-3 text-right space-x-2">
                            {!v.is_active && <button onClick={() => handleApplyVersion(switchModal.key, v.id)} className="text-primary hover:text-primary text-sm">应用</button>}
                            <button onClick={() => handleDeleteVersion(switchModal.key, v.id)} className="text-red-500 hover:text-red-700 text-sm">删除</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="mt-4 pt-3 border-t text-center">
                  <button onClick={() => {
                    handleEdit(prompts.find(p => p.key === switchModal.key)!);
                    setSwitchModal(null);
                  }} className="text-sm text-primary hover:text-primary">+ 基于系统默认新建版本</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── 变量复制按钮 ──

const varAnnotations: Record<string, string> = {
  project_info: '项目信息（名称、编号、采购方式等）',
  context_info: '上下文信息（上级章节概要）',
  chapter_id: '章节编号',
  chapter_title: '章节标题',
  chapter_description: '章节描述',
  children_info: '子章节列表及其描述',
  project_context: '项目背景概述',
  overview: '项目概述',
  requirements: '技术评分要求',
  old_outline: '已有目录结构（用户上传的旧方案）',
  file_content: '文档原文内容（用户上传的文件文本）',
  chapter_info: '章节编号和标题',
  references: '系统搜索到的参考资料',
  originality_hint: '重新生成时强调原创性的提示',
};

const VariableCopyBtn: React.FC<{ varName: string }> = ({ varName }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const text = `{${varName}}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className={`inline-block text-xs px-2 py-0.5 rounded mr-1 mb-0.5 transition-colors ${
        copied ? 'bg-green-100 text-green-700' : 'bg-accent text-foreground hover:bg-primary/10 hover:text-primary'
      }`}
      title={varAnnotations[varName] || varName}
    >
      <span>{`{${varName}}`}</span>
      {copied && <span className="ml-1 text-green-600 font-medium">已复制</span>}
    </button>
  );
};

export default UserPrompts;
